import React, { useState, useEffect } from "react";
import { THEME } from "../../constants/theme";

const FeatureHintWrapper = ({
    children,
    hintKey,
    location,
    hintConfigs, // Fetched hint configurations from the backend
}) => {
    const [showHint, setShowHint] = useState(false);
    const [hintContent, setHintContent] = useState("");
    const [hintConfig, setHintConfig] = useState(null);
    const [isLoggedIn, setIsLoggedIn] = useState(false);

    useEffect(() => {
        const isLoggedIn = localStorage.getItem("userAddress");
        if (isLoggedIn) {
            setIsLoggedIn(true);
        } else {
            setIsLoggedIn(false);
        }
        // Check if any other hints are currently shown
        const activeHints = document.querySelectorAll(".feature-hint-active");
        if (activeHints.length > 0) {
            return; // Don't show new hint if others are active
        }

        checkAndUpdateHint();

        const pollInterval = setInterval(checkAndUpdateHint, 30000);
        return () => clearInterval(pollInterval);
    }, [hintKey, location]);

    const checkAndUpdateHint = () => {
        // Try to get configs from localStorage first
        const storedConfigs = localStorage.getItem("hintConfigs");
        const configs = storedConfigs ? JSON.parse(storedConfigs) : hintConfigs;

        const config = configs?.find(
            (config) => config.key === hintKey && config.location === location && config.isActive
        );

        if (config) {
            const storedDismissTime = localStorage.getItem(`${hintKey}_dismissed_at`);
            const currentTime = new Date().getTime();
            const dismissExpiry = config.dismissDuration || 7 * 24 * 60 * 60 * 1000; // Default 7 days

            if (!storedDismissTime || currentTime - parseInt(storedDismissTime) > dismissExpiry) {
                setShowHint(true);
                setHintContent(config.content);
                setHintConfig(config);
            }
        }
    };
    if (!isLoggedIn) {
        return children;
    }
    const wrapperStyle = hintConfig?.highlightBorder
        ? {
              padding: "4px",
              border: `2px solid white`,
              borderRadius: THEME.borderRadius.md,
              transition: "border-color 0.3s ease",
          }
        : {};

    const renderImage = () => {
        if (!hintConfig) return null;
        const imageUrl = location.includes("banner") ? hintConfig.imageUrl : hintConfig.iconUrl;
        if (!imageUrl) return null;

        return (
            <img
                src={imageUrl}
                alt=""
                style={{
                    maxWidth: location.includes("banner") ? "100%" : "24px",
                    marginBottom: "10px",
                }}
            />
        );
    };

    const closeHint = () => {
        setShowHint(false);
        localStorage.setItem(`${hintKey}_dismissed_at`, new Date().getTime().toString());
    };

    if (!showHint) {
        return children;
    }

    // Render the hint based on the location
    const renderHint = () => {
        const baseHintStyle = {
            backgroundColor: THEME.colors.background.DEFAULT,
            padding: "1rem",
            border: `1px solid ${THEME.colors.text.primary}`,
            borderRadius: THEME.borderRadius.md,
            boxShadow: THEME.boxShadow.md,
            maxWidth: "100%",
            zIndex: 150,
            boxSizing: "border-box",
            position: "relative",
            className: "feature-hint-active",
        };

        const buttonStyle = {
            marginTop: "0.75rem",
            padding: "0.5rem 1rem",
            borderRadius: THEME.borderRadius.md,
            border: "none",
            backgroundColor: THEME.colors.text.secondary,
            color: THEME.colors.white,
            cursor: "pointer",
            height: "36px",
            minWidth: "80px",
            fontSize: "0.875rem",
            transition: "background-color 0.2s ease",
        };

        switch (location) {
            case "button":
                return (
                    <div
                        style={{
                            position: "relative",
                            display: "inline-block",
                            ...wrapperStyle,
                        }}
                    >
                        {children}
                        <div
                            style={{
                                ...baseHintStyle,
                                position: "absolute",
                                top: "100%",
                                left: "50%",
                                transform: "translateX(-50%)",
                                marginTop: "0.75rem",
                                width: "max-content",
                                maxWidth: "300px",
                            }}
                        >
                            {renderImage()}
                            <p style={{ margin: "0.5rem 0", color: THEME.colors.text }}>
                                {hintContent}
                            </p>
                            <button
                                onClick={closeHint}
                                style={buttonStyle}
                                onMouseOver={(e) =>
                                    (e.target.style.backgroundColor = THEME.colors.primaryHover)
                                }
                                onMouseOut={(e) =>
                                    (e.target.style.backgroundColor = THEME.colors.primary)
                                }
                            >
                                Got it!
                            </button>
                        </div>
                    </div>
                );
            case "top-banner":
                return (
                    <div style={{ width: "100%", position: "relative" }}>
                        <div
                            style={{
                                ...baseHintStyle,
                                marginBottom: "20px",
                                textAlign: "center",
                                maxWidth: "800px",
                                margin: "0 auto 20px",
                            }}
                        >
                            {renderImage()}
                            <p style={{ margin: "8px 0" }}>{hintContent}</p>
                            <button
                                onClick={closeHint}
                                style={buttonStyle}
                                onMouseOver={(e) =>
                                    (e.target.style.backgroundColor = THEME.colors.primaryHover)
                                }
                                onMouseOut={(e) =>
                                    (e.target.style.backgroundColor = THEME.colors.primary)
                                }
                            >
                                Got it!
                            </button>
                        </div>
                        {children}
                    </div>
                );
            case "sidebar":
                return (
                    <>
                        {children}
                        <div
                            style={{
                                ...baseHintStyle,
                                position: "fixed",
                                top: "50%",
                                right: "20px",
                                transform: "translateY(-50%)",
                                width: "300px",
                            }}
                        >
                            {renderImage()}
                            <p style={{ margin: "8px 0" }}>{hintContent}</p>
                            <button onClick={closeHint} style={buttonStyle}>
                                Got it!
                            </button>
                        </div>
                    </>
                );
            default:
                return children;
        }
    };

    return renderHint();
};

export default FeatureHintWrapper;
